import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getCallHistory } from "@/api/services";
import Header from "@/components/Header";
import { Phone, Video, Clock, Calendar, Loader2, Search, PhoneIncoming, PhoneOutgoing, PhoneMissed } from "lucide-react";
import { cn } from "@/utils/cn";
import { format } from "date-fns";

const getMaskedId = (id) => {
  if (!id) return "Client";
  const shortId = id.toString().slice(-6).toUpperCase();
  return `#${shortId}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
};

export default function CallsPage() {
  const { astrologer } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!astrologer?.astrologerId || !astrologer?.accessToken) return;

    setLoading(true);
    getCallHistory(astrologer.astrologerId, astrologer.accessToken)
      .then(({ data }) => {
        if (data?.appointments) {
          setCalls(data.appointments);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [astrologer]);

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDuration = (mins) => {
    if (!mins) return "-";
    if (mins >= 60) {
      return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    }
    return `${mins}m`;
  };

  const getCallIcon = (status, mode) => {
    if (status === "completed") return mode === "video" ? Video : PhoneIncoming;
    if (status === "expired" || status === "cancelled") return PhoneMissed;
    return PhoneOutgoing;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "text-green-600";
      case "pending": return "text-yellow-600";
      case "cancelled": 
      case "expired": return "text-red-500";
      default: return "text-gray-600";
    }
  };

  const filteredCalls = calls
    .filter(call => filter === "all" || call.status === filter)
    .filter(call => 
      !searchTerm || 
      call.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getMaskedId(call.user?._id).toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const stats = {
    total: calls.length,
    completed: calls.filter(c => c.status === "completed").length,
    missed: calls.filter(c => c.status === "expired" || c.status === "cancelled").length,
    totalMinutes: calls.filter(c => c.status === "completed").reduce((sum, c) => sum + (c.duration || 0), 0),
  };

  return (
    <>
      <Header name="Call Log" />
      <div className="w-full p-4">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500">Total Calls</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            <p className="text-xs text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-500">{stats.missed}</p>
            <p className="text-xs text-gray-500">Missed</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{formatDuration(stats.totalMinutes)}</p>
            <p className="text-xs text-gray-500">Talk Time</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm mb-4">
          <div className="p-3 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex gap-1">
              {["all", "completed", "expired"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-lg transition-colors",
                    filter === f ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  {f === "all" ? "All" : f === "completed" ? "Completed" : "Missed"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Call List */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Phone className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No calls found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredCalls.map((call) => {
                const CallIcon = getCallIcon(call.status, call.mode);
                return (
                  <div key={call._id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                      {call.user?.picture ? (
                        <img src={call.user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <span className="text-sm font-medium">{getInitials(call.user?.name)}</span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{call.user?.name || "Unknown"}</p>
                        <span className="text-xs text-gray-400">{getMaskedId(call.user?._id)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span>{format(new Date(call.date), "MMM d, yyyy")}</span>
                        <span>·</span>
                        <span>{formatTime(call.time)}</span>
                        {call.duration > 0 && (
                          <>
                            <span>·</span>
                            <Clock className="h-3 w-3" />
                            <span>{formatDuration(call.duration)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Status Icon */}
                    <div className={cn("flex items-center gap-2", getStatusColor(call.status))}>
                      <CallIcon className="h-5 w-5" />
                      <span className="text-xs hidden sm:inline">
                        {call.status === "completed" ? "Completed" : call.status === "expired" ? "Missed" : call.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
